/**
 * Offline Storage Service using IndexedDB
 * Handles caching of products, clients, and pending sync queue
 */

const DB_NAME = 'iltzar_offline';
const DB_VERSION = 1;

const STORES = {
  PRODUCTS: 'products',
  CLIENTS: 'clients',
  PENDING_ORDERS: 'pending_orders',
  PENDING_CLIENTS: 'pending_clients',
  SYNC_META: 'sync_meta'
};

let db = null;

/**
 * Initialize IndexedDB
 */
const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Error opening IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Products store
      if (!database.objectStoreNames.contains(STORES.PRODUCTS)) {
        const productsStore = database.createObjectStore(STORES.PRODUCTS, { keyPath: 'id' });
        productsStore.createIndex('category', 'category', { unique: false });
        productsStore.createIndex('type', 'type', { unique: false });
      }

      // Clients store
      if (!database.objectStoreNames.contains(STORES.CLIENTS)) {
        const clientsStore = database.createObjectStore(STORES.CLIENTS, { keyPath: 'id' });
        clientsStore.createIndex('name', 'name', { unique: false });
        clientsStore.createIndex('type', 'type', { unique: false });
      }

      // Pending orders queue
      if (!database.objectStoreNames.contains(STORES.PENDING_ORDERS)) {
        const ordersStore = database.createObjectStore(STORES.PENDING_ORDERS, { keyPath: 'localId', autoIncrement: true });
        ordersStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // Pending clients queue
      if (!database.objectStoreNames.contains(STORES.PENDING_CLIENTS)) {
        const pendingClientsStore = database.createObjectStore(STORES.PENDING_CLIENTS, { keyPath: 'localId', autoIncrement: true });
        pendingClientsStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // Sync metadata
      if (!database.objectStoreNames.contains(STORES.SYNC_META)) {
        database.createObjectStore(STORES.SYNC_META, { keyPath: 'key' });
      }
    };
  });
};

/**
 * Generic store operations
 */
const getStore = async (storeName, mode = 'readonly') => {
  const database = await initDB();
  const transaction = database.transaction(storeName, mode);
  return transaction.objectStore(storeName);
};

const getAllFromStore = async (storeName) => {
  return new Promise(async (resolve, reject) => {
    try {
      const store = await getStore(storeName);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    } catch (error) {
      reject(error);
    }
  });
};

const putInStore = async (storeName, data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const store = await getStore(storeName, 'readwrite');
      const request = store.put(data);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    } catch (error) {
      reject(error);
    }
  });
};

const deleteFromStore = async (storeName, key) => {
  return new Promise(async (resolve, reject) => {
    try {
      const store = await getStore(storeName, 'readwrite');
      const request = store.delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    } catch (error) {
      reject(error);
    }
  });
};

const clearStore = async (storeName) => {
  return new Promise(async (resolve, reject) => {
    try {
      const store = await getStore(storeName, 'readwrite');
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    } catch (error) {
      reject(error);
    }
  });
};

const bulkPutInStore = async (storeName, items) => {
  return new Promise(async (resolve, reject) => {
    try {
      const database = await initDB();
      const transaction = database.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);

      items.forEach(item => store.put(item));

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Products operations
 */
export const cacheProducts = async (products) => {
  try {
    await clearStore(STORES.PRODUCTS);
    await bulkPutInStore(STORES.PRODUCTS, products);
    await putInStore(STORES.SYNC_META, {
      key: 'products_last_sync',
      timestamp: Date.now()
    });
    console.log(`Cached ${products.length} products offline`);
    return true;
  } catch (error) {
    console.error('Error caching products:', error);
    return false;
  }
};

export const getCachedProducts = async () => {
  try {
    return await getAllFromStore(STORES.PRODUCTS);
  } catch (error) {
    console.error('Error getting cached products:', error);
    return [];
  }
};

export const getProductsLastSync = async () => {
  try {
    const store = await getStore(STORES.SYNC_META);
    return new Promise((resolve) => {
      const request = store.get('products_last_sync');
      request.onsuccess = () => resolve(request.result?.timestamp || null);
      request.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
};

/**
 * Clients operations
 */
export const cacheClients = async (clients) => {
  try {
    await clearStore(STORES.CLIENTS);
    await bulkPutInStore(STORES.CLIENTS, clients);
    await putInStore(STORES.SYNC_META, {
      key: 'clients_last_sync',
      timestamp: Date.now()
    });
    console.log(`Cached ${clients.length} clients offline`);
    return true;
  } catch (error) {
    console.error('Error caching clients:', error);
    return false;
  }
};

export const getCachedClients = async () => {
  try {
    return await getAllFromStore(STORES.CLIENTS);
  } catch (error) {
    console.error('Error getting cached clients:', error);
    return [];
  }
};

export const addCachedClient = async (client) => {
  try {
    await putInStore(STORES.CLIENTS, client);
    return true;
  } catch (error) {
    console.error('Error adding cached client:', error);
    return false;
  }
};

export const getClientsLastSync = async () => {
  try {
    const store = await getStore(STORES.SYNC_META);
    return new Promise((resolve) => {
      const request = store.get('clients_last_sync');
      request.onsuccess = () => resolve(request.result?.timestamp || null);
      request.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
};

/**
 * Pending orders queue
 */
export const addPendingOrder = async (orderData) => {
  try {
    const pendingOrder = {
      ...orderData,
      createdAt: Date.now(),
      syncStatus: 'pending'
    };
    const localId = await putInStore(STORES.PENDING_ORDERS, pendingOrder);
    console.log('Order added to offline queue:', localId);
    return localId;
  } catch (error) {
    console.error('Error adding pending order:', error);
    throw error;
  }
};

export const getPendingOrders = async () => {
  try {
    return await getAllFromStore(STORES.PENDING_ORDERS);
  } catch (error) {
    console.error('Error getting pending orders:', error);
    return [];
  }
};

export const removePendingOrder = async (localId) => {
  try {
    await deleteFromStore(STORES.PENDING_ORDERS, localId);
    return true;
  } catch (error) {
    console.error('Error removing pending order:', error);
    return false;
  }
};

export const getPendingOrdersCount = async () => {
  try {
    const orders = await getAllFromStore(STORES.PENDING_ORDERS);
    return orders.length;
  } catch {
    return 0;
  }
};

/**
 * Pending clients queue
 */
export const addPendingClient = async (clientData) => {
  try {
    const pendingClient = {
      ...clientData,
      localId: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: Date.now(),
      syncStatus: 'pending'
    };
    await putInStore(STORES.PENDING_CLIENTS, pendingClient);
    // Also add to local cache so it appears in the client list
    await addCachedClient({
      ...pendingClient,
      id: pendingClient.localId,
      isLocal: true
    });
    console.log('Client added to offline queue:', pendingClient.localId);
    return pendingClient;
  } catch (error) {
    console.error('Error adding pending client:', error);
    throw error;
  }
};

export const getPendingClients = async () => {
  try {
    return await getAllFromStore(STORES.PENDING_CLIENTS);
  } catch (error) {
    console.error('Error getting pending clients:', error);
    return [];
  }
};

export const removePendingClient = async (localId) => {
  try {
    await deleteFromStore(STORES.PENDING_CLIENTS, localId);
    return true;
  } catch (error) {
    console.error('Error removing pending client:', error);
    return false;
  }
};

export const updateLocalClientWithServerId = async (localId, serverClient) => {
  try {
    // Remove from pending
    await removePendingClient(localId);
    // Update in cache with server data
    await putInStore(STORES.CLIENTS, serverClient);
    return true;
  } catch (error) {
    console.error('Error updating local client:', error);
    return false;
  }
};

export const getPendingClientsCount = async () => {
  try {
    const clients = await getAllFromStore(STORES.PENDING_CLIENTS);
    return clients.length;
  } catch {
    return 0;
  }
};

/**
 * Sync status
 */
export const getSyncStatus = async () => {
  const pendingOrders = await getPendingOrdersCount();
  const pendingClients = await getPendingClientsCount();
  const productsSync = await getProductsLastSync();
  const clientsSync = await getClientsLastSync();

  return {
    pendingOrders,
    pendingClients,
    hasPendingData: pendingOrders > 0 || pendingClients > 0,
    lastProductsSync: productsSync,
    lastClientsSync: clientsSync
  };
};

/**
 * Initialize the database on module load
 */
initDB().catch(console.error);

export default {
  cacheProducts,
  getCachedProducts,
  getProductsLastSync,
  cacheClients,
  getCachedClients,
  addCachedClient,
  getClientsLastSync,
  addPendingOrder,
  getPendingOrders,
  removePendingOrder,
  getPendingOrdersCount,
  addPendingClient,
  getPendingClients,
  removePendingClient,
  updateLocalClientWithServerId,
  getPendingClientsCount,
  getSyncStatus
};
